/**
 * @fileoverview Router configuration for the application using React Router.
 * @module config/router
 */

import { lazy, Suspense } from "react"
import { createBrowserRouter, RouterProvider } from "react-router-dom"

import App from "@/App"
import { Spinner } from "@/components/atoms"
import { RouteErrorElement } from "@/components/organisms"

// Route-level code splitting: each page ships in its own chunk.
const Home = lazy(() => import("@/pages/Home"))
const Analytics = lazy(() => import("@/pages/Analytics"))
const NotFound = lazy(() => import("@/pages/NotFound"))

/**
 * Wraps a lazily loaded page in its Suspense fallback.
 *
 * @param Page - The lazily imported page component.
 * @returns The element to hand to the route definition.
 */
function withSuspense(Page: React.ComponentType) {
	return (
		<Suspense
			fallback={<Spinner size="lg" label="Chargement de la page…" />}
		>
			<Page />
		</Suspense>
	)
}

/**
 * Router configuration.
 *
 * @description
 * Every branch declares the same `errorElement`, so a failure anywhere in the
 * tree lands on the single centralised error screen.
 */
const router = createBrowserRouter([
	{
		path: "/",
		element: <App />,
		errorElement: <RouteErrorElement />,
		children: [
			{
				index: true,
				element: withSuspense(Home),
			},
			{
				path: "analytics",
				element: withSuspense(Analytics),
			},
			{
				path: "*",
				element: withSuspense(NotFound),
				loader: () => {
					// Surfaces unmatched routes as a real 404 for the boundary.
					throw new Response("Page non trouvée", {
						status: 404,
						statusText: "Not Found",
					})
				},
			},
		],
	},
])

/**
 * Router component.
 *
 * @returns The RouterProvider wired with the configured routes.
 */
export default function Router() {
	return <RouterProvider router={router} />
}
